import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Lock, Save, ChevronRight, Search, Check, X, ShieldCheck,
  Mail, Phone, MapPin, Clock, AlertCircle, FileText,
  FolderKanban, TrendingUp, DollarSign, Sun, FileSignature,
} from "lucide-react";
import ProjectPhotoTimeline from "@/components/ProjectPhotoTimeline";
import NewInvoiceModal from "@/components/NewInvoiceModal";
import KpiCard, { KpiGrid } from "@/components/KpiCard";
import EmptyState from "@/components/EmptyState";
import RowActions from "@/components/RowActions";
import ClientHoverCard from "@/components/ClientHoverCard";

function InternalNotes({ client, onSaved }) {
  const [notes, setNotes] = useState(client.internal_notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNotes(client.internal_notes || ""); }, [client.user_id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/users/${client.user_id}/internal-notes`, { notes });
      toast.success("Notes saved");
      onSaved?.(r.data);
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-[#E2E8F0] dark:border-slate-700 rounded-md p-4 bg-amber-50 dark:bg-amber-900/10" data-testid="internal-notes">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-xs uppercase tracking-wider font-bold text-amber-700 dark:text-amber-400">Internal notes (admin only)</span>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        maxLength={5000}
        placeholder="Private context: discount agreements, follow-up dates, special handling instructions, etc. Never visible to the client."
        data-testid="internal-notes-input"
        className="w-full border border-amber-200 dark:border-amber-700/50 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm bg-white"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-[#94A3B8]">{notes.length}/5000 chars</span>
        <button onClick={save} disabled={saving} data-testid="save-notes" className="btn-primary text-xs py-1.5 px-3">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save notes"}
        </button>
      </div>
    </div>
  );
}

// ---------- Relative-time helper for signup age ----------
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function signupSeverity(iso) {
  if (!iso) return "medium";
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours >= 48) return "critical";
  if (hours >= 8) return "high";
  return "medium";
}

// ---------- Rich pending-client card with inline approve / reject ----------
function PendingClientCard({ client, onApprove, onReject, busy }) {
  const sev = signupSeverity(client.created_at);
  const dotClass = {
    critical: "bg-red-500",
    high:     "bg-amber-500",
    medium:   "bg-sky-500",
  }[sev];
  const ageLabel = timeAgo(client.created_at);
  return (
    <div
      data-testid={`pending-card-${client.user_id}`}
      className="rounded-xl border border-amber-200 dark:border-amber-700/40 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-sm hover:shadow-md transition"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Avatar / initials */}
        <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading text-base font-semibold uppercase">
          {(client.name || client.email || "?").trim().charAt(0)}
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-[15px] sm:text-base font-medium text-[#0A2540] dark:text-white truncate max-w-full">
              {client.name || "Unnamed client"}
            </h3>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full text-white ${dotClass}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
              {sev === "critical" ? "urgent" : sev === "high" ? "waiting" : "new"}
            </span>
          </div>
          <div className="mt-1 flex flex-col gap-1 text-[13px] text-[#475569] dark:text-slate-400">
            <div className="flex items-center gap-1.5 min-w-0">
              <Mail className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{client.email || "—"}</span>
            </div>
            {client.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 shrink-0" />
                <span>{client.phone}</span>
              </div>
            )}
            {client.address && (
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="line-clamp-1">{client.address}</span>
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap text-[11px] text-[#94A3B8] mt-1">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Signed up {ageLabel}
              </span>
              {client.customer_code && (
                <span className="font-mono text-[#C9A356]">{client.customer_code}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions — full-width on mobile, inline on desktop */}
      <div className="mt-4 grid grid-cols-2 sm:flex sm:justify-end gap-2">
        <button
          onClick={() => onReject(client)}
          disabled={busy}
          data-testid={`pending-reject-${client.user_id}`}
          className="inline-flex items-center justify-center gap-1.5 px-4 h-11 sm:h-9 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-[13px] font-semibold text-[#475569] dark:text-slate-300 hover:border-red-500 hover:text-red-600 dark:hover:text-red-400 transition disabled:opacity-50"
        >
          <X className="w-4 h-4" /> Reject
        </button>
        <Link
          to={`/admin/clients/${client.user_id}`}
          data-testid={`pending-review-${client.user_id}`}
          className="hidden sm:inline-flex items-center justify-center gap-1.5 px-4 h-9 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-[13px] font-semibold text-[#0A2540] dark:text-white hover:border-[#C9A356] transition"
        >
          Review profile
        </Link>
        <button
          onClick={() => onApprove(client)}
          disabled={busy}
          data-testid={`pending-approve-${client.user_id}`}
          className="inline-flex items-center justify-center gap-1.5 px-4 h-11 sm:h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold shadow-sm transition disabled:opacity-50"
        >
          <Check className="w-4 h-4" /> Approve
        </button>
      </div>
    </div>
  );
}

// ---------- Approval queue — collapsible banner ----------
function PendingApprovalQueue({ pending, onApprove, onReject, onApproveAll, busy }) {
  const [expanded, setExpanded] = useState(false);
  if (pending.length === 0) return null;
  const critical = pending.filter((c) => signupSeverity(c.created_at) === "critical").length;
  return (
    <section
      data-testid="pending-approval-queue"
      className="rounded-xl border border-amber-300/60 dark:border-amber-800/40 bg-gradient-to-br from-amber-50/90 to-white dark:from-amber-900/10 dark:to-slate-900/60 mb-6 overflow-hidden"
    >
      {/* Compact banner — always visible */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4">
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="toggle-pending-queue"
          className="flex items-center gap-3 min-w-0 text-left group"
          aria-expanded={expanded}
        >
          <div className="w-9 h-9 rounded-lg bg-amber-500 grid place-items-center text-white shrink-0">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-[#0A2540] dark:text-white">
                {pending.length} awaiting approval
              </span>
              {critical > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500 text-white">
                  <AlertCircle className="w-3 h-3" /> {critical} &gt;48h
                </span>
              )}
              <span className="text-[11px] text-[#94A3B8] group-hover:text-[#0A2540] dark:group-hover:text-white">
                {expanded ? "Hide" : "Review"} →
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-[#475569] dark:text-slate-400 mt-0.5 truncate">
              New signups can't book or see packages until you approve.
            </p>
          </div>
        </button>
        {pending.length > 1 && (
          <button
            onClick={onApproveAll}
            disabled={busy}
            data-testid="approve-all-pending"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition disabled:opacity-50 shrink-0"
          >
            <Check className="w-3.5 h-3.5" /> Approve all
          </button>
        )}
      </div>

      {expanded && (
        <div className="p-3 sm:p-4 pt-0 grid gap-3 sm:grid-cols-2 border-t border-amber-300/40 dark:border-amber-800/30">
          {pending.map((c) => (
            <PendingClientCard
              key={c.user_id}
              client={c}
              busy={busy}
              onApprove={onApprove}
              onReject={onReject}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function AdminClients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [invoicing, setInvoicing] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");   // all | pending | approved
  const [busy, setBusy] = useState(false);
  const [smartFilter, setSmartFilter] = useState(null);   // null | 'open_invoice' | 'no_projects' | 'this_month'
  const [enrich, setEnrich] = useState({});                // uid → {open_inv, project_count}

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  // Enrich each client with counts once, so filters are instant.
  useEffect(() => {
    if (clients.length === 0) return;
    let cancelled = false;
    Promise.all([
      api.get("/invoices").then((r) => r.data).catch(() => []),
      api.get("/projects").then((r) => r.data).catch(() => []),
    ]).then(([invs, projs]) => {
      if (cancelled) return;
      const map = {};
      for (const c of clients) map[c.user_id] = { open_inv: 0, project_count: 0 };
      for (const i of invs) {
        if (!map[i.user_id]) continue;
        if (!["paid", "void", "denied"].includes(i.status)) map[i.user_id].open_inv += 1;
      }
      for (const p of projs) {
        if (!map[p.user_id]) continue;
        map[p.user_id].project_count += 1;
      }
      setEnrich(map);
    });
    return () => { cancelled = true; };
  }, [clients]);

  // Auto-select client from ?focus=<user_id> (used by notification bell + Today card)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (focus && clients.length > 0) {
      const target = clients.find((c) => c.user_id === focus);
      if (target) {
        setSelected(target);
        // If the focused client is pending, keep the queue in view.
        if (target.access_level !== "approved") setTab("pending");
      }
    }
  }, [clients]);

  const pending  = useMemo(() => clients.filter((c) => c.access_level !== "approved"), [clients]);
  const approved = useMemo(() => clients.filter((c) => c.access_level === "approved"), [clients]);

  const filteredForTab = tab === "pending" ? pending : tab === "approved" ? approved : clients;
  const filteredForSmart = useMemo(() => {
    if (!smartFilter) return filteredForTab;
    const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0,0,0,0);
    return filteredForTab.filter((c) => {
      const e = enrich[c.user_id] || { open_inv: 0, project_count: 0 };
      if (smartFilter === "open_invoice")  return e.open_inv > 0;
      if (smartFilter === "no_projects")   return e.project_count === 0 && c.access_level === "approved";
      if (smartFilter === "this_month") {
        if (!c.created_at) return false;
        return new Date(c.created_at) >= startOfMonth;
      }
      return true;
    });
  }, [filteredForTab, smartFilter, enrich]);

  const visibleClients = query.trim()
    ? filteredForSmart.filter((c) => {
        const q = query.trim().toLowerCase();
        return (
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.customer_code || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q)
        );
      })
    : filteredForSmart;

  const approve = async (c) => {
    setBusy(true);
    try {
      const r = await api.post(`/users/${c.user_id}/approve`);
      toast.success(`${c.name || c.email} approved`);
      if (selected?.user_id === c.user_id) setSelected(r.data);
      await load();
    } catch {
      toast.error("Approve failed");
    } finally { setBusy(false); }
  };

  const reject = async (c) => {
    if (!window.confirm(`Reject ${c.name || c.email}? Their account will be removed and they'll need to sign up again.`)) return;
    setBusy(true);
    try {
      await api.post(`/users/${c.user_id}/reject`);
      toast.success(`${c.name || c.email} rejected`);
      if (selected?.user_id === c.user_id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  const revoke = async (uid) => {
    if (!window.confirm("Revoke this client's full access?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/users/${uid}/revoke`);
      toast.success("Access revoked");
      setSelected(r.data);
      await load();
    } catch {
      toast.error("Could not revoke access. Please try again.");
    } finally { setBusy(false); }
  };

  const approveAll = async () => {
    if (!window.confirm(`Approve all ${pending.length} pending clients?`)) return;
    setBusy(true);
    try {
      await Promise.all(pending.map((c) => api.post(`/users/${c.user_id}/approve`)));
      toast.success(`Approved ${pending.length} client${pending.length === 1 ? "" : "s"}`);
      await load();
    } catch {
      toast.error("Some approvals failed — refreshing");
      await load();
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const header = ["Name", "Email", "Phone", "PB-Code", "Status", "Joined"];
    const rows = visibleClients.map((c) => [
      c.name || "",
      c.email || "",
      c.phone || "",
      c.customer_code || "",
      c.access_level || "",
      c.created_at ? new Date(c.created_at).toISOString().split("T")[0] : "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `powerbuilt-clients-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-clients">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <p className="overline">Customer base</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Clients</h1>
        </div>
      </div>

      {/* Pending approval queue — hero section */}
      <PendingApprovalQueue
        pending={pending}
        onApprove={approve}
        onReject={reject}
        onApproveAll={approveAll}
        busy={busy}
      />

      {/* Tabs + search bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden" role="tablist">
          {[
            { key: "all",      label: `All (${clients.length})` },
            { key: "pending",  label: `Pending (${pending.length})` },
            { key: "approved", label: `Approved (${approved.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`clients-tab-${t.key}`}
              className={`px-4 h-10 text-[13px] font-semibold transition ${
                tab === t.key
                  ? "bg-[#0A2540] text-white"
                  : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, or PB-code"
            data-testid="clients-search"
            className="w-full pl-9 pr-3 h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 text-sm placeholder:text-[#94A3B8]"
          />
        </div>
      </div>

      {/* Smart filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="smart-filters">
        {[
          { key: null,             label: "No filter" },
          { key: "open_invoice",   label: "Has open invoice" },
          { key: "no_projects",    label: "No projects yet" },
          { key: "this_month",     label: "Joined this month" },
        ].map((f) => (
          <button
            key={String(f.key)}
            onClick={() => setSmartFilter(f.key)}
            data-testid={`filter-${f.key || "none"}`}
            className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border transition ${
              smartFilter === f.key
                ? "bg-[#C9A356] text-white border-[#C9A356]"
                : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-300 border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Full-width CRM database */}
      <div className="card-pb overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 flex items-center justify-between">
          <h3 className="font-heading text-sm text-[#0A2540] dark:text-white">
            {visibleClients.length} {tab === "all" ? "client" : tab} record{visibleClients.length === 1 ? "" : "s"}
          </h3>
          <button
            onClick={exportCsv}
            data-testid="export-clients-csv"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
          >
            Export CSV
          </button>
        </div>

        {/* Desktop table */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 dark:bg-slate-900/60 text-left">
            <tr className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">PB-code</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleClients.map((c) => {
              const isPending = c.access_level !== "approved";
              return (
                <tr
                  key={c.user_id}
                  data-testid={`client-row-${c.user_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading font-semibold uppercase shrink-0 text-sm">
                        {(c.name || c.email || "?").trim().charAt(0)}
                      </div>
                      <ClientHoverCard client={c} enrich={enrich[c.user_id]}>
                        <button
                          type="button"
                          onClick={() => setSelected(c)}
                          className="text-left min-w-0"
                          data-testid={`client-open-${c.user_id}`}
                        >
                          <div className="font-medium text-[#0A2540] dark:text-white truncate hover:text-[#C9A356]">
                            {c.name || "Unnamed"}
                          </div>
                          {c.phone && (
                            <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">{c.phone}</div>
                          )}
                        </button>
                      </ClientHoverCard>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#475569] dark:text-slate-400 max-w-[240px] truncate">{c.email || "—"}</td>
                  <td className="px-4 py-3">
                    {isPending ? (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300" data-testid={`pending-badge-${c.user_id}`}>
                        Pending
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                        Approved
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#C9A356]">{c.customer_code || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#475569] dark:text-slate-400">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <ClientRowActions
                      client={c}
                      onView={() => setSelected(c)}
                      onInvoice={() => { setSelected(c); setInvoicing(true); }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-[#E2E8F0] dark:divide-slate-800" data-testid="clients-mobile-list">
          {visibleClients.map((c) => {
            const isPending = c.access_level !== "approved";
            return (
              <li
                key={c.user_id}
                data-testid={`client-card-${c.user_id}`}
                className="p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading font-semibold uppercase shrink-0">
                    {(c.name || c.email || "?").trim().charAt(0)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(c)}
                    className="min-w-0 flex-1 text-left"
                    data-testid={`client-open-card-${c.user_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#0A2540] dark:text-white truncate">
                        {c.name || "Unnamed"}
                      </span>
                      {isPending && (
                        <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#475569] dark:text-slate-400 truncate">{c.email}</div>
                    <div className="flex items-center gap-2 text-[11px] text-[#94A3B8] mt-0.5">
                      {c.customer_code && <span className="font-mono text-[#C9A356]">{c.customer_code}</span>}
                      {c.phone && <span>· {c.phone}</span>}
                    </div>
                  </button>
                </div>
                <div className="flex gap-2 mt-3">
                  <ClientRowActions
                    client={c}
                    fullWidth
                    onView={() => setSelected(c)}
                    onInvoice={() => { setSelected(c); setInvoicing(true); }}
                  />
                </div>
              </li>
            );
          })}
        </ul>

        {visibleClients.length === 0 && (
          <div className="p-10 text-center text-sm text-[#475569] dark:text-slate-400">
            {tab === "pending" ? "No pending clients 🎉" : "No clients match your search"}
          </div>
        )}
      </div>

      {/* Slide-in detail drawer */}
      {selected && (
        <ClientDetailDrawer
          client={selected}
          busy={busy}
          onClose={() => setSelected(null)}
          onInvoice={() => setInvoicing(true)}
          onApprove={() => approve(selected)}
          onRevoke={() => revoke(selected.user_id)}
          onSaved={(updated) => { setSelected(updated); load(); }}
        />
      )}

      <NewInvoiceModal
        open={invoicing && !!selected}
        onClose={() => setInvoicing(false)}
        onCreated={() => { setInvoicing(false); toast.success("Draft ready in Invoices"); }}
        prefillClientId={selected?.user_id}
      />
    </div>
  );
}

// ---------- Per-row action bar ----------
function ClientRowActions({ client, onView, onInvoice, fullWidth }) {
  const approved = client.access_level === "approved";
  return (
    <div className={`inline-flex items-center justify-end gap-1 ${fullWidth ? "w-full" : ""}`}>
      <button
        type="button"
        onClick={onView}
        data-testid={`row-view-${client.user_id}`}
        className={`inline-flex items-center justify-center gap-1 h-8 px-3 rounded-md text-xs font-semibold bg-[#0A2540] text-white hover:bg-[#12365b] transition ${fullWidth ? "flex-1" : ""}`}
      >
        Open
      </button>
      <RowActions
        testId={`client-actions-${client.user_id}`}
        items={[
          {
            label: approved ? "Draft invoice" : "Approve first",
            icon: FileText,
            onClick: onInvoice,
            disabled: !approved,
            testId: `row-invoice-${client.user_id}`,
          },
          {
            label: "Open full profile",
            icon: FileSignature,
            onClick: () => { window.location.href = `/admin/clients/${client.user_id}`; },
            testId: `row-profile-${client.user_id}`,
          },
        ]}
      />
    </div>
  );
}

// ---------- Slide-in client detail drawer ----------
function ClientDetailDrawer({
  client, busy, onClose, onInvoice, onApprove, onRevoke, onSaved,
}) {
  const [summary, setSummary] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Fetch the 360° payload so we can show "what they have" (projects, invoices,
  // requests, quotes, payments) inline instead of forcing a page nav.
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    api.get(`/admin/clients/${client.user_id}/360`)
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => { if (!cancelled) setSummary({ projects: [], invoices: [], requests: [], payments: [], work_orders: [] }); });
    return () => { cancelled = true; };
  }, [client.user_id]);

  const stats = useMemo(() => {
    const s = summary || { projects: [], invoices: [], requests: [], payments: [] };
    const openInv = (s.invoices || []).filter((i) => !["paid", "void", "denied"].includes(i.status)).length;
    const activeProj = (s.projects || []).filter((p) => !["completed", "cancelled"].includes(p.status)).length;
    const openReq = (s.requests || []).filter((r) => !["completed", "cancelled", "closed"].includes(r.status)).length;
    const totalPaid = (s.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
    return { openInv, activeProj, openReq, totalPaid,
             totalProj: (s.projects || []).length,
             totalInv: (s.invoices || []).length,
             totalReq: (s.requests || []).length };
  }, [summary]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="client-drawer"
    >
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-[#E2E8F0] dark:border-slate-700 p-4 sm:p-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="overline">{client.customer_code || "—"}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <h2 className="font-heading text-xl sm:text-2xl text-[#0A2540] dark:text-white truncate">{client.name || "Unnamed"}</h2>
              {client.access_level === "approved" ? (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" data-testid="status-approved">✓ Approved</span>
              ) : (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300" data-testid="status-pending">Pending</span>
              )}
            </div>
            <p className="text-sm text-[#475569] dark:text-slate-400 truncate">{client.email}</p>
            {client.phone && <p className="text-xs text-[#475569] dark:text-slate-400">{client.phone}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="drawer-close"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-[#475569] dark:text-slate-400 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/admin/clients/${client.user_id}`}
              className="btn-primary text-sm text-center justify-center"
              data-testid="open-360-btn"
            >
              Full profile <ChevronRight className="w-4 h-4 inline ml-1" />
            </Link>
            <button
              onClick={onInvoice}
              disabled={client.access_level !== "approved"}
              className="btn-accent text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="new-invoice-for-client-btn"
            >
              <FileText className="w-4 h-4" /> Send invoice
            </button>
            {client.access_level !== "approved" ? (
              <button
                onClick={onApprove}
                disabled={busy}
                className="col-span-2 h-11 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
                data-testid="approve-btn"
              >
                ✓ Approve full access
              </button>
            ) : (
              <button
                onClick={onRevoke}
                disabled={busy}
                className="col-span-2 btn-outline-navy text-sm"
                data-testid="revoke-btn"
              >
                Revoke access
              </button>
            )}
            <button
              onClick={() => setShowCreate(!showCreate)}
              disabled={client.access_level !== "approved"}
              className="col-span-2 h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-xs font-semibold text-[#475569] dark:text-slate-300 hover:border-[#0A2540] dark:hover:border-white disabled:opacity-50"
              data-testid="new-project-btn"
            >
              {showCreate ? "Cancel new project" : "+ New project"}
            </button>
          </div>

          {showCreate && (
            <NewProjectForm
              client={client}
              onCreated={() => {
                setShowCreate(false);
                // Refresh 360° so the new project appears immediately.
                api.get(`/admin/clients/${client.user_id}/360`)
                   .then((r) => setSummary(r.data)).catch(() => {});
              }}
            />
          )}

          {/* What they have */}
          <section>
            <p className="overline mb-2">What they have</p>
            <div className="grid grid-cols-3 gap-2">
              <SummaryTile
                label="Projects"
                total={stats.totalProj}
                sub={`${stats.activeProj} active`}
                to={`/admin/projects?focus=user:${client.user_id}`}
                testId="drawer-tile-projects"
              />
              <SummaryTile
                label="Invoices"
                total={stats.totalInv}
                sub={`${stats.openInv} open`}
                to={`/admin/payments?client=${client.user_id}`}
                testId="drawer-tile-invoices"
              />
              <SummaryTile
                label="Requests"
                total={stats.totalReq}
                sub={`${stats.openReq} open`}
                to={`/admin/requests?focus=${client.user_id}`}
                testId="drawer-tile-requests"
              />
            </div>
            <div className="mt-2 text-xs text-[#475569] dark:text-slate-400 text-right">
              Lifetime paid: <span className="tabular-nums font-semibold text-[#0A2540] dark:text-white">${stats.totalPaid.toFixed(2)}</span>
            </div>
          </section>

          {/* Latest activity strip */}
          {summary && (summary.projects?.length || summary.invoices?.length) ? (
            <section className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 divide-y divide-[#E2E8F0] dark:divide-slate-700 text-sm">
              {(summary.projects || []).slice(0, 3).map((p) => (
                <Link
                  key={p.project_id}
                  to={`/admin/projects?focus=${p.project_id}`}
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  data-testid={`drawer-link-project-${p.project_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-[#94A3B8]">Project</div>
                    <div className="text-[#0A2540] dark:text-white truncate">{p.title}</div>
                  </div>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[#475569] dark:text-slate-300 shrink-0">
                    {p.status || "open"}
                  </span>
                </Link>
              ))}
              {(summary.invoices || []).slice(0, 3).map((i) => (
                <Link
                  key={i.invoice_id}
                  to={`/admin/invoices/${i.invoice_id}`}
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  data-testid={`drawer-link-invoice-${i.invoice_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-[#94A3B8]">Invoice · {i.invoice_id}</div>
                    <div className="text-[#0A2540] dark:text-white truncate">{i.title || "—"}</div>
                  </div>
                  <span className="text-sm tabular-nums font-semibold text-[#0A2540] dark:text-white shrink-0">
                    ${Number(i.grand_total ?? i.amount ?? 0).toFixed(2)}
                  </span>
                </Link>
              ))}
            </section>
          ) : null}

          {client.address && (
            <div className="rounded-md border border-[#E2E8F0] dark:border-slate-700 p-3 text-sm">
              <p className="overline mb-1">Address</p>
              <p className="text-[#0A2540] dark:text-white">{client.address}</p>
            </div>
          )}

          <InternalNotes client={client} onSaved={onSaved} />
        </div>
      </div>
    </div>
  );
}

// ---------- Small "what they have" tile ----------
function SummaryTile({ label, total, sub, to, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block rounded-md border border-[#E2E8F0] dark:border-slate-700 p-3 hover:border-[#C9A356] transition group"
    >
      <p className="overline">{label}</p>
      <div className="font-heading text-2xl font-bold text-[#0A2540] dark:text-white tabular-nums leading-none mt-1">{total}</div>
      <div className="text-[10px] text-[#475569] dark:text-slate-400 mt-0.5 group-hover:text-[#C9A356]">{sub}</div>
    </Link>
  );
}

// ---------- Industry-standard new-project form ----------
function NewProjectForm({ client, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    project_type: "solar_install",
    scope: "",
    site_address: client.address || "",
    kw_target: "",
    amount: "",
    start_date: "",
    target_completion: "",
    priority: "normal",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const isSolar = form.project_type === "solar_install" || form.project_type === "solar_upgrade";

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Add a project title"); return; }
    setSaving(true);
    try {
      await api.post("/projects", {
        user_id: client.user_id,
        title: form.title.trim(),
        project_type: form.project_type,
        amount: parseFloat(form.amount) || 0,
        scope: form.scope.trim(),
        site_address: form.site_address.trim(),
        kw_target: isSolar && form.kw_target ? parseFloat(form.kw_target) : undefined,
        start_date: form.start_date || undefined,
        target_completion: form.target_completion || undefined,
        priority: form.priority,
        notes: form.notes.trim(),
      });
      toast.success("Project created");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-900/60"
      data-testid="create-project-form"
    >
      <h3 className="font-heading text-sm font-medium text-[#0A2540] dark:text-white mb-3">
        New project for {client.name}
      </h3>

      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Project title *</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. 5kW Rooftop Solar Install"
            data-testid="proj-title"
            className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Service type *</label>
            <select
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              data-testid="proj-type"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
            >
              <option value="solar_install">Solar Installation</option>
              <option value="solar_upgrade">Solar Upgrade / Expansion</option>
              <option value="electrical">Electrical Work</option>
              <option value="maintenance">System Maintenance</option>
              <option value="battery">Battery / Backup</option>
              <option value="inspection">Inspection / Audit</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              data-testid="proj-priority"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Scope of work</label>
          <textarea
            value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value })}
            rows={2}
            placeholder="Short scope description the crew will see on their work order."
            data-testid="proj-scope"
            className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-800"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Site address</label>
          <input
            value={form.site_address}
            onChange={(e) => setForm({ ...form, site_address: e.target.value })}
            placeholder="Where the crew will work"
            data-testid="proj-site-address"
            className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {isSolar && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">System size (kW)</label>
              <input
                type="number" min="0" step="0.1"
                value={form.kw_target}
                onChange={(e) => setForm({ ...form, kw_target: e.target.value })}
                placeholder="e.g. 5.4"
                data-testid="proj-kw"
                className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
              />
            </div>
          )}
          <div className={isSolar ? "" : "col-span-2"}>
            <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Estimated value (USD)</label>
            <input
              type="number" min="0" step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="Optional — helps forecast revenue"
              data-testid="proj-amount"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Start date</label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              data-testid="proj-start-date"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Target completion</label>
            <input
              type="date"
              value={form.target_completion}
              onChange={(e) => setForm({ ...form, target_completion: e.target.value })}
              data-testid="proj-target-date"
              className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 h-10 text-sm bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 font-semibold block mb-1">Internal notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Internal-only context, not shown to the client."
            data-testid="proj-notes"
            className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-800"
          />
        </div>

        <button
          onClick={submit}
          disabled={saving || !form.title.trim()}
          className="btn-accent text-sm w-full h-11 justify-center inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="create-project-submit"
        >
          {saving ? "Creating…" : "Create project"}
        </button>
      </div>
    </div>
  );
}
